'use client';
import { useState, useEffect } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import ButtonNormal from '@/components/common/ui/buttons/ButtonNormal';

const CookieModal = styled(motion.div)`
  position: fixed;
  bottom: 20px;
  right: 20px;
  width: 380px;
  max-width: calc(100vw - 40px);
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(20px);
  border-radius: 16px;
  padding: 24px;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.15);
  border: 1px solid rgba(255, 255, 255, 0.2);
  z-index: 10000;
  
  @media (max-width: 1024px) {
    width: 360px;
    bottom: 20px;
    right: 20px;
  }
  
  @media (max-width: 768px) {
    width: calc(100vw - 40px);
    max-width: 400px;
    bottom: 20px;
    right: 20px;
    left: 20px;
    margin: 0 auto;
    padding: 20px;
  }
  
  @media (max-width: 480px) {
    width: calc(100vw - 24px);
    bottom: 12px;
    right: 12px;
    left: 12px;
    margin: 0 auto;
    padding: 16px;
    border-radius: 12px;
  }
  
  @media (max-width: 320px) {
    width: calc(100vw - 16px);
    bottom: 8px;
    right: 8px;
    left: 8px;
    padding: 12px;
  }
`;

const Title = styled.h3`
  font-size: 20px;
  font-weight: 600;
  color: #1a1a1a;
  margin: 0 0 12px 0;
  line-height: 1.3;
  
  @media (max-width: 480px) {
    font-size: 18px;
    margin: 0 0 10px 0;
  }
  
  @media (max-width: 320px) {
    font-size: 16px;
    margin: 0 0 8px 0;
  }
`;

const Description = styled.p`
  font-size: 14px;
  color: #666;
  line-height: 1.5;
  margin: 0 0 20px 0;
  
  @media (max-width: 480px) {
    font-size: 13px;
    margin: 0 0 16px 0;
    line-height: 1.4;
  }
  
  @media (max-width: 320px) {
    font-size: 12px;
    margin: 0 0 12px 0;
  }
`;

const ButtonContainer = styled.div`
  display: flex;
  gap: 12px;
  
  @media (max-width: 480px) {
    flex-direction: column;
    gap: 10px;
  }
  
  @media (max-width: 320px) {
    gap: 8px;
  }
  
  .dc-btn {
    flex: 1;
    
    a {
      width: 100%;
      padding: 10px 20px;
      
      @media (max-width: 480px) {
        padding: 12px 20px;
        font-size: 14px;
      }
      
      @media (max-width: 320px) {
        padding: 10px 16px;
        font-size: 13px;
      }
    }
  }
`;

const CookieConsent = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [hasConsented, setHasConsented] = useState(null);

  useEffect(() => {
    // Check if user has already made a choice
    const cookieConsent = localStorage.getItem('cookieConsent');
    
    if (!cookieConsent) {
      // Show modal after a longer delay
      const timer = setTimeout(() => {
        setIsVisible(true);
      }, 2500);
      
      return () => clearTimeout(timer);
    } else {
      setHasConsented(cookieConsent === 'accepted');
    }
  }, []);

  const handleAccept = () => {
    localStorage.setItem('cookieConsent', 'accepted');
    setHasConsented(true);
    setIsVisible(false);
    
    // Enable analytics or other tracking services here
    enableAnalytics();
  };

  const handleReject = () => {
    localStorage.setItem('cookieConsent', 'rejected');
    setHasConsented(false);
    setIsVisible(false);
    
    // Disable analytics or other tracking services here
    disableAnalytics();
  };

  const enableAnalytics = () => {
    // Google Analytics or other tracking services
    if (typeof window !== 'undefined' && window.gtag) {
      window.gtag('consent', 'update', {
        'analytics_storage': 'granted',
        'ad_storage': 'granted'
      });
    }
    
    // Set cookie for analytics
    document.cookie = "analytics_enabled=true; path=/; max-age=31536000; SameSite=Lax";
    
  };

  const disableAnalytics = () => {
    // Disable tracking services
    if (typeof window !== 'undefined' && window.gtag) {
      window.gtag('consent', 'update', {
        'analytics_storage': 'denied',
        'ad_storage': 'denied'
      });
    }
    
    // Set cookie for analytics
    document.cookie = "analytics_enabled=false; path=/; max-age=31536000; SameSite=Lax";
    
  };

  return (
    <AnimatePresence>
      {isVisible && (
        <CookieModal
          initial={{ opacity: 0, y: 100 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 100 }}
          transition={{
            duration: 0.8,
            ease: [0.25, 0.46, 0.45, 0.94]
          }}
        >
          <Title>We value your privacy</Title>
          <Description>
            We use cookies to enhance your browsing experience, serve personalised 
            ads or content, and analyse our traffic. By clicking "Accept All", you consent 
            to our use of cookies.
          </Description>
          
          <ButtonContainer>
            <ButtonNormal 
              text="Reject All"
              onClick={handleReject}
              submit={true}
              background="transparent"
              color="#152637"
              borderColor="#152637"
              hoverBackground="#152637"
              hoverColor="#fff"
              fontSize="14px"
              fontWeight="600"
              borderRadius={8}
            />
            <ButtonNormal 
              text="Accept All"
              onClick={handleAccept}
              submit={true}
              background="#ED2024"
              color="#fff"
              borderColor="#ED2024"
              hoverBackground="#152637"
              hoverColor="#fff"
              fontSize="14px"
              fontWeight="600"
              borderRadius={8}
            />
          </ButtonContainer>
        </CookieModal>
      )}
    </AnimatePresence>
  );
};

export default CookieConsent;
